import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { Link } from 'src/domains/layouts/webview/components/router/NewLink';
import { withConfig } from 'src/withConfig';

interface PriceLegendWrapperPropsType {
    isHomePage?: boolean;
    variant: DefaultComponentVariantType | null;
}

const setPriceLegendWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            margin-top: 8px;
            padding: ${theme.star.padding.xMedium} ${theme.star.padding.xMedium} 0;
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            margin-top: auto;
            display: flex;
            flex-flow: column;
            gap: ${theme.star.padding.xMedium};
            padding: ${theme.star.padding.xMedium};
            font-size: ${theme.star.fontSize.small};
            background-color: inherit;
        `;
    }
    return css`
        border: 1px solid ${theme.star.priceLegendList.borderColor};
        padding: ${theme.star.padding.medium} ${theme.star.padding.medium} 0;
    `;
};

export const PriceLegendWrapper = withConfig(theme => styled('div', { label: 'PriceLegendWrapper' })<PriceLegendWrapperPropsType>`
    margin-top: -1px;
    font-size: ${theme.star.fontSize.xSmall};
    background-color: ${theme.star.priceLegendList.bgColor};
    ${({ variant }): SerializedStyles => setPriceLegendWrapper(variant, theme)}
    color: ${theme.star.priceLegendList.txtColor};
    font-weight: ${theme.star.fontWeight.regular};
    position: relative;
`);

const setPriceLegend = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            display: flex;
            gap: ${theme.star.gap.large};
            flex-wrap: wrap;

            & [class$='PriceLegendItem'] {
                display: flex;
                align-items: center;
                gap: ${theme.star.gap.medium};
                margin: 0 !important;
                padding: 0;

                &::before {
                    position: relative;
                    border-radius: ${theme.star.radius.small};
                }
            }
        `;
    }
    return css``;
};

export const PriceLegend = withConfig(theme => styled('ul', { label: 'PriceLegend' })<{variant: DefaultComponentVariantType | null}>`
    line-height: 1;
    ${({ variant }): SerializedStyles => setPriceLegend(variant, theme)}
`);

interface PriceLegendItemPropsType {
    variant: DefaultComponentVariantType | null;
    itemColor: string;
    type: string
}

export const PriceLegendItem = withConfig(theme => styled('li', { label: 'PriceLegendItem' })<PriceLegendItemPropsType>`
    display: inline-block;
    line-height: 1.4;
    margin-bottom: 9px;
    padding-left: 20px;
    position: relative;
    &:not(:last-of-type) {
        margin-right: 16px;
    }

    .suspended--icon {
        rect {
            fill: ${theme.star.unified.priceLegendList.statusFourth};
        }
    }

    ${({ variant, type, itemColor }) =>
        !(variant === DefaultComponentVariantType.TERTIARY && type === 'suspended') &&
        `&::before {
            background: ${itemColor};
            bottom: 0;
            content: '';
            height: 16px;
            left: 0;
            margin: auto 0;
            position: absolute;
            top: 0;
            width: 16px;
        }`
      }
`);

export const InPlayRedirect = withConfig(theme => styled(Link, { label: 'InPlayRedirect' })`
    text-decoration: none;
    font-weight: ${theme.star.fontWeight.bold};
    margin-left: 4px;
    color: ${theme.star.priceLegendList.txtColor};
`);

const setInPlayRedirectWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            display: flex;
            gap: ${theme.star.gap.small};
            margin: 0;
            line-height: 1.333;

            & [class$='InPlayRedirect'] {
                margin: 0;
                color: ${theme.star.priceLegendList.txtColor};
            }
        `;
    }
    return css`
        margin: 0 0 10px;
        line-height: 1.4;
    `;
};

export const InPlayRedirectWrapper = withConfig(theme => styled('p', { label: 'InPlayRedirectWrapper' })<{variant: DefaultComponentVariantType | null}>`
    ${({variant}): SerializedStyles => setInPlayRedirectWrapper(variant, theme)};
`);
